import type { IReactionDisposer } from 'mobx';
import { comparer, toJS, makeAutoObservable, observable, reaction, runInAction, set, when } from 'mobx';

import { WELL_DIRECTORY_SERVICE_API_URL } from '../../shared/api/constants';
import type { CurvePoint } from '../../shared/api/CurvesCacheService';
import { CurvesCacheService } from '../../shared/api/CurvesCacheService';
import { DashboardService } from '../../shared/api/DashboardService';
import type {
  DrillingAnalysisGroup,
  Formation,
  Sequence,
  StratUnit,
  Wellbore,
} from '../../shared/api/IDashboardService';
import type { LogCurve, WellIndexType } from '../../shared/api/IWellDirectoryService';
import { WellDirectoryService } from '../../shared/api/WellDirectoryService';
import { BaseParameterTypes } from '../../shared/baseParams';
import type { IClassOfUnitsService } from '../../shared/IClassOfUnitsService';
import { CurveCoordinatesService } from '../../shared/services/curve-coordinates.service';
import { UnitManager } from '../../shared/services/unit-manager';
import { WellIntervalsService } from '../../shared/services/well-intervals.service';
import type { WellLogWidgetStompPublisher } from '../../shared/stompTypes';
import { IntervalOptionsStore } from '../../shared/stores/interval-options.store';
import { Wellbores } from '../../shared/stores/WellboresStore';
import { WellLogs } from '../../shared/stores/WellLogs';
import { hasValue } from '../../shared/utils';
import { findHeatmapCorridorMatches } from '../components/HeatMapChart/corridorOverlayUtils';
import { DEFAULT_MODES, DEPTH_MAX, DEPTH_MIN } from '../const';
import type {
  HeatmapCorridorOverlay,
  HeatmapWidgetAppParams,
  HeatmapWidgetEventHandlers,
  HeatmapWidgetInitParams,
  HeatmapWidgetStateParams,
} from '../types';

import { HeatmapChartStore } from './ChartStore/HeatmapChartStore';
import { HeatmapWidgetStatePublisher } from './HeatmapWidgetStatePublisher';
import { HeatmapDataProcessor } from './services/HeatmapDataProcessor';
import { HeatmapWidgetSettingsStore } from './SettingsStore/HeatmapWidgetSettingsStore';
import type { HeatmapWidgetAxisParams } from './types';
import { IntervalsTypes, OperationRegimes } from './types';

const HEATMAP_BASE_PARAMS: BaseParameterTypes[] = [
  BaseParameterTypes.WEIGHT_ON_BIT,
  BaseParameterTypes.ROTARY_SPEED,
  BaseParameterTypes.RATE_OF_PENETRATION,
  BaseParameterTypes.BIT_SPEED,
  BaseParameterTypes.BIT_CONTACT_PRESSURE,
  BaseParameterTypes.MECH_SPEC_ENERGY_BIT,
  BaseParameterTypes.MECH_SPEC_ENERGY_SURFACE,
];

export class HeatmapWidgetState {
  eventHandlers: HeatmapWidgetEventHandlers;

  statePublisher: HeatmapWidgetStatePublisher;

  appParams: HeatmapWidgetAppParams;

  stompPublisher: WellLogWidgetStompPublisher;

  private dashboardService: DashboardService;

  private wellDirectoryService: WellDirectoryService;

  private curvesCacheService: CurvesCacheService;

  classOfUnitsService: IClassOfUnitsService;

  isSettingsDrawerOpen = false;

  settings: HeatmapWidgetSettingsStore;

  wellbores: Wellbores;

  logs: WellLogs;

  status: 'initial' | 'loading' | 'ready' | 'failed' = 'initial';

  wellId: number;

  curvesData: Partial<Record<BaseParameterTypes, LogCurve>> | null = null;

  heatmapChartStore: HeatmapChartStore;

  index: WellIndexType = 'LOG_DEPTH';

  curveCoordinates: Partial<Record<BaseParameterTypes, CurvePoint[]>> | null = null;

  currentFormationId: number | null = null;

  currentDrillingAnalysisGroupId: number | null = null;

  currentStratUnitId: number | null = null;

  currentSectionId: number | null = null;

  stratUnits: StratUnit[] = [];

  formations: Formation[] = [];

  drillingAnalysisGroups: DrillingAnalysisGroup[] = [];

  sections: Sequence[] = [];

  intervalOptionsStore: IntervalOptionsStore;

  private unitManager = new UnitManager();

  private dataService: WellIntervalsService;

  private curveService: CurveCoordinatesService;

  private dataProcessor: HeatmapDataProcessor;

  unsubTimeout: ReturnType<typeof setTimeout> | null = null;

  private disposers: IReactionDisposer[] = [];

  private loadVersion = 0;

  private destroyed = false;

  private loadError: unknown = null;

  errorMessage: string | null = null;

  get isSuppressTooltip() {
    return this.appParams.resizing === true || this.appParams.moving === true || this.appParams.inFocus === false;
  }

  constructor(
    appParams: HeatmapWidgetAppParams,
    initParams: HeatmapWidgetInitParams,
    initState: Partial<HeatmapWidgetStateParams>,
    eventHandlers: HeatmapWidgetEventHandlers,
  ) {
    this.statePublisher = new HeatmapWidgetStatePublisher(initState, eventHandlers?.onInternalStateChanged);
    this.eventHandlers = eventHandlers;
    this.appParams = appParams;
    this.stompPublisher = initParams.stompPublisher;
    this.wellId = appParams.wellId;

    this.classOfUnitsService = initParams.classOfUnitsService;
    this.dashboardService = new DashboardService(initParams.axiosInstance);
    this.intervalOptionsStore = new IntervalOptionsStore(this.dashboardService);
    this.wellDirectoryService = new WellDirectoryService(initParams.axiosInstance, WELL_DIRECTORY_SERVICE_API_URL);
    this.wellbores = new Wellbores(this.dashboardService);
    this.logs = new WellLogs(this.wellDirectoryService, initParams.wellDirectoryServiceStompPublisher);
    this.dataService = new WellIntervalsService(this.dashboardService);
    this.curvesCacheService = new CurvesCacheService(initParams.legacyAxiosInstance);
    this.curveService = new CurveCoordinatesService(
      this.wellDirectoryService,
      this.curvesCacheService,
      this.stompPublisher,
      () => this.index,
      (curveId, coordinates) => this.handleCurveCoordinatesChanged(curveId, coordinates),
    );
    this.dataProcessor = new HeatmapDataProcessor(this.unitManager);
    this.settings = new HeatmapWidgetSettingsStore(
      this.classOfUnitsService,
      this.unitManager,
      this.appParams.isAltNames,
      initParams.defaultDepthUnits,
      this.statePublisher,
      initState,
    );
    this.heatmapChartStore = new HeatmapChartStore(this.settings);

    makeAutoObservable<this, 'disposers' | 'loadError'>(this, {
      curveCoordinates: observable.ref,
      disposers: false,
      loadError: false,
    });

    this.disposers = [
      reaction(
        () => ({
          interval: this.settings.interval,
          operationRegimes: this.settings.operationRegimes,
        }),
        ({ interval, operationRegimes }, prev) => {
          const intervalChanged = prev.interval !== interval;
          const drillingAnalysisGroupRegimeChanged =
            interval === IntervalsTypes.DRILLING_ANALYSIS && prev.operationRegimes !== operationRegimes;
          if (!intervalChanged && !drillingAnalysisGroupRegimeChanged) {
            return;
          }
          if (interval === IntervalsTypes.USER_DEFINED) {
            this.clearCurrentIntervalSelection();
            this.publishSelectedIntervalId(null);
            return;
          }
          if (interval === IntervalsTypes.FORMATIONS && this.formations.length > 0) {
            this.setCurrentFormationId(this.formations[0].id);
          } else if (
            interval === IntervalsTypes.DRILLING_ANALYSIS &&
            this.drillingAnalysisGroupsForCurrentRegime.length > 0
          ) {
            this.setCurrentDrillingAnalysisGroupId(this.drillingAnalysisGroupsForCurrentRegime[0].id);
          } else if (interval === IntervalsTypes.STRAT_UNITS && this.stratUnits.length > 0) {
            this.setCurrentStratUnitId(this.stratUnits[0].id);
          } else if (interval === IntervalsTypes.SECTIONS && this.sections.length > 0) {
            this.setCurrentSectionId(this.sections[0].id);
          } else {
            this.clearCurrentIntervalSelection();
            this.publishSelectedIntervalId(null);
          }
        },
      ),
      reaction(
        () => ({
          axes: toJS(this.settings.axisParams),
          filters: toJS(this.settings.zFilterParams),
          startDepth: this.settings.startDepth,
          endDepth: this.settings.endDepth,
          depthUnit: this.settings.depthUnit,
          lastBinsRange: this.settings.lastBinsRange,
          operationRegimes: this.settings.operationRegimes,
          rotorDrilling: this.settings.rotorDrilling,
          slideDrilling: this.settings.slideDrilling,
        }),
        () => this.updateHeatmapChartData(),
        { equals: comparer.structural },
      ),
      reaction(
        () => this.appParams.active,
        (active) => {
          if (!active) {
            this.clearSubTimeout();
            this.unsubTimeout = setTimeout(() => {
              this.curveService.unsubscribeAll();
              this.clearSubTimeout();
            }, 30 * 1000);
          } else {
            this.clearSubTimeout();
            const subscribed = this.updateCurveSubscriptions();
            if (subscribed) {
              const version = this.loadVersion;
              this.curveService
                .loadCurves(this.curvesData, true)
                .catch((error: unknown) => this.failLoad(version, error));
            }
          }
        },
      ),
      reaction(
        () => this.isCurveDataLoading,
        (loading) => {
          if (!loading && this.status === 'ready') {
            this.updateHeatmapChartData();
          }
        },
      ),
      this.init(),
    ];
  }

  setAppParams(appParams: Partial<HeatmapWidgetAppParams>) {
    const nextWellId = appParams.wellId;

    set(this.appParams, appParams);

    if (typeof appParams.isAltNames === 'boolean') {
      this.settings.setAltNames(appParams.isAltNames);
    }
    if (typeof nextWellId === 'number' && nextWellId !== this.wellId) {
      this.setWell(nextWellId).catch(console.error);
    }
  }

  setWellboreById(wellboreId: number) {
    const wellbore = this.wellbores.getWellboreById(wellboreId);
    if (wellbore) {
      this.loadWell(this.wellId, wellbore).catch(console.error);
    }
  }

  private init() {
    this.setWell(this.wellId).catch(() => {});
    this.intervalOptionsStore.load();
    return when(
      () =>
        this.status === 'failed' ||
        (this.status === 'ready' &&
          !this.isDataLoading &&
          ['ready', 'failed'].includes(this.intervalOptionsStore.status)),
      () => {
        if (this.status === 'failed') {
          this.eventHandlers.onInitializationFailed?.(
            'Failed to initialize heatmap',
            this.loadError instanceof Error ? this.loadError : undefined,
          );
        } else {
          this.eventHandlers.onInitializedSuccess?.();
        }
      },
    );
  }

  private beginLoad() {
    this.loadVersion += 1;
    this.clearSubTimeout();
    this.curveService.reset();
    this.logs.clear();
    this.logs.inProgress = false;
    this.wellbores.inProgress = false;
    this.clearHeatmapData();
    this.curvesData = null;
    this.settings.setCurvesData(null);
    this.status = 'loading';
    this.errorMessage = null;
    this.loadError = null;
    return this.loadVersion;
  }

  private isCurrentLoad(version: number) {
    return !this.destroyed && version === this.loadVersion && this.errorMessage === null;
  }

  private failLoad(version: number, error: unknown) {
    if (!this.isCurrentLoad(version)) {
      return;
    }
    console.error(error);
    runInAction(() => {
      this.loadError = error;
      this.status = 'failed';
      this.errorMessage = 'Heatmap-load-error';
      this.logs.clear();
      this.wellbores.clear();
      this.logs.inProgress = false;
      this.wellbores.inProgress = false;
      this.curveService.reset();
      this.clearHeatmapData();
    });
  }

  retry() {
    this.setWell(this.wellId).catch(console.error);
  }

  setWell(wellId: number) {
    return this.loadWell(wellId);
  }

  setWellbores(wellId: number) {
    return this.loadWell(wellId);
  }

  private async loadWell(wellId: number, wellbore?: Wellbore) {
    if (this.destroyed) {
      return;
    }
    const cachedSections = wellbore && this.status === 'ready' ? this.sections : null;
    const version = this.beginLoad();
    if (wellId !== this.wellId) {
      this.resetSettingsForWellChange();
    }
    this.wellId = wellId;
    if (!wellbore) {
      this.wellbores.clear();
      this.sections = [];
    }
    try {
      const [sections] = await Promise.all([
        cachedSections ??
          this.dataService.fetchSections(wellId).catch((error: unknown) => {
            console.error('Failed to load well sections', error);
            return [];
          }),
        this.loadWellboreData(wellId, version, wellbore),
      ]);
      if (!this.isCurrentLoad(version)) {
        return;
      }
      runInAction(() => {
        this.sections = sections;
        this.restoreSelectedIntervalFromState();
        this.status = 'ready';
        this.updateHeatmapChartData();
      });
    } catch (error) {
      this.failLoad(version, error);
      if (!this.destroyed && version === this.loadVersion) {
        throw error;
      }
    }
  }

  private async loadWellboreData(wellId: number, version: number, wellbore?: Wellbore) {
    if (!wellbore) {
      this.wellbores.inProgress = true;
      const wellbores = await this.dashboardService.getWellbores(wellId);
      if (!this.isCurrentLoad(version)) {
        return;
      }
      runInAction(() => {
        this.wellbores.list = wellbores;
        this.wellbores.inProgress = false;
      });
      wellbore =
        this.wellbores.getDefaultSelected(
          this.appParams.defaultSelectWellbore === 'active',
          this.statePublisher.params.wellboreId ?? undefined,
        ) ?? undefined;
    }
    if (!wellbore) {
      runInAction(() => {
        this.clearStratigraphy();
        this.clearCurrentIntervalSelection();
        this.statePublisher.setParams({
          wellboreId: null,
          selectedIntervalId: null,
        });
      });
      return;
    }
    const selected = wellbore;
    runInAction(() => {
      this.wellbores.setSelected(selected);
      this.statePublisher.setParams({ wellboreId: selected.id });
      this.clearCurrentIntervalSelection();
      this.clearStratigraphy();
    });
    this.eventHandlers.onWellboreChanged?.(wellbore.specialName);
    const [, { stratUnits, formations }, groups] = await Promise.all([
      this.loadLogsAndBaseParameters(wellId, wellbore.id, version),
      this.dataService.fetchStratigraphyHeaders(wellbore.id).catch((error: unknown) => {
        console.error('Failed to load stratigraphy headers', error);
        return { stratUnits: [], formations: [] };
      }),
      this.dataService.fetchDrillingAnalysis(wellbore.id).catch((error: unknown) => {
        console.error('Failed to load drilling analysis groups', error);
        return [];
      }),
    ]);
    if (!this.isCurrentLoad(version)) {
      return;
    }
    runInAction(() => {
      this.stratUnits = stratUnits;
      this.formations = formations;
      this.drillingAnalysisGroups = groups;
    });
  }

  private async loadLogsAndBaseParameters(wellId: number, wellboreId: number, version: number) {
    runInAction(() => {
      this.logs.inProgress = true;
    });
    const logs = await this.wellDirectoryService.getLogs(wellId, wellboreId, this.index);
    if (!this.isCurrentLoad(version)) {
      return;
    }
    runInAction(() => {
      this.logs.setLogs(wellId, wellboreId, logs);
      this.logs.inProgress = false;
    });
    await this.fetchBaseParameters(wellboreId, version);
  }

  private clearStratigraphy() {
    this.stratUnits = [];
    this.formations = [];
    this.drillingAnalysisGroups = [];
  }

  get drillingAnalysisGroupsForCurrentRegime(): DrillingAnalysisGroup[] {
    const drillingMode = this.settings.operationRegimes === OperationRegimes.ROTATION ? 'ROTARY' : 'SLIDING';
    return this.drillingAnalysisGroups.filter((group) => group.drillingMode === drillingMode);
  }

  private convertDepthToSelectedUnit(value?: number | null, uomId?: number | null): number {
    if (value == null) {
      return 0;
    }
    if (!uomId) {
      return value;
    }
    const unit = this.classOfUnitsService.findUnitById(uomId);
    if (!unit) {
      return value;
    }
    const fromUnit = unit.abbreviation;
    const targetUnit = this.settings.depthUnit;

    if (!fromUnit || fromUnit === targetUnit) {
      return value;
    }
    const converted = this.unitManager.convert(fromUnit, targetUnit, value);
    return Math.round(converted * 10) / 10;
  }

  private convertMetersToSelectedUnit(value: number): number {
    const converted = this.unitManager.convert('m', this.settings.depthUnit, value);
    return Math.round(converted * 10) / 10;
  }

  setCurrentFormationId(formationId: number) {
    const formation = this.formations.find((item) => item.id === formationId);
    if (!formation) {
      this.clearCurrentIntervalSelection();
      this.publishSelectedIntervalId(null);
      return;
    }
    this.settings.setDepthRange({
      startDepth: this.convertDepthToSelectedUnit(formation.mdTop?.value, formation.mdTop?.uomId),
      endDepth:
        formation.mdBottom?.value == null
          ? null
          : this.convertDepthToSelectedUnit(formation.mdBottom.value, formation.mdBottom.uomId),
    });
    this.clearCurrentIntervalSelection();
    this.currentFormationId = formationId;
    this.publishSelectedIntervalId(this.currentFormationId);
  }

  get currentDrillingAnalysisGroup(): DrillingAnalysisGroup | null {
    const group = this.drillingAnalysisGroupsForCurrentRegime.find(
      (item) => item.id === this.currentDrillingAnalysisGroupId,
    );
    return group || null;
  }

  get corridorOverlayForHeatmap(): HeatmapCorridorOverlay | null {
    if (!this.currentDrillingAnalysisGroup) {
      return null;
    }
    const matches = findHeatmapCorridorMatches(
      this.currentDrillingAnalysisGroup,
      this.settings.currentXAxisParameter,
      this.settings.currentYAxisParameter,
    );

    if (!matches.x || !matches.y) {
      return null;
    }

    const xBounds = this.convertCorridorBoundsToAxis(
      matches.x,
      this.settings.currentXAxisParameter,
      this.settings.currentXAxisParams,
    );
    const yBounds = this.convertCorridorBoundsToAxis(
      matches.y,
      this.settings.currentYAxisParameter,
      this.settings.currentYAxisParams,
    );

    if (!xBounds || !yBounds) {
      return null;
    }

    return { x: xBounds, y: yBounds };
  }

  private convertCorridorBoundsToAxis(
    corridor: { floor: number; ceiling: number; uomId: number },
    parameter: BaseParameterTypes,
    axisParams: HeatmapWidgetAxisParams,
  ): { floor: number; ceiling: number } | null {
    const curveUnit = this.curvesData?.[parameter]?.unit.abbreviation;
    const axisUnit = axisParams.unit.abbreviation || curveUnit || null;

    const floor = this.convertCorridorValueToAxis(corridor.floor, corridor.uomId, axisUnit);
    const ceiling = this.convertCorridorValueToAxis(corridor.ceiling, corridor.uomId, axisUnit);

    if (floor == null || ceiling == null) {
      return null;
    }

    return { floor, ceiling };
  }

  private convertCorridorValueToAxis(
    value: number | null | undefined,
    corridorUomId: number,
    axisUnit: string | null,
  ): number | null {
    if (value == null) {
      return null;
    }

    const corridorUnit = this.classOfUnitsService.findUnitById(corridorUomId);
    if (!corridorUnit) {
      return value;
    }

    if (!axisUnit || corridorUnit.abbreviation === axisUnit) {
      return value;
    }

    return this.unitManager.convert(corridorUnit.abbreviation, axisUnit, value);
  }

  setCurrentDrillingAnalysisGroupId(groupId: number) {
    const group = this.drillingAnalysisGroupsForCurrentRegime.find((item) => item.id === groupId);
    if (!group) {
      this.clearCurrentIntervalSelection();
      this.publishSelectedIntervalId(null);
      return;
    }
    this.settings.setDepthRange({
      startDepth: this.convertMetersToSelectedUnit(group.fromDepth),
      endDepth: group.toDepth == null ? null : this.convertMetersToSelectedUnit(group.toDepth),
    });
    this.clearCurrentIntervalSelection();
    this.currentDrillingAnalysisGroupId = groupId;
    this.publishSelectedIntervalId(this.currentDrillingAnalysisGroupId);
  }

  setCurrentStratUnitId(stratUnitId: number) {
    const stratUnit = this.stratUnits.find((item) => item.id === stratUnitId);
    if (!stratUnit) {
      this.clearCurrentIntervalSelection();
      this.publishSelectedIntervalId(null);
      return;
    }
    this.settings.setDepthRange({
      startDepth: this.convertDepthToSelectedUnit(stratUnit.mdTop?.value, stratUnit.mdTop?.uomId),
      endDepth:
        stratUnit.mdBottom?.value == null
          ? null
          : this.convertDepthToSelectedUnit(stratUnit.mdBottom.value, stratUnit.mdBottom.uomId),
    });
    this.clearCurrentIntervalSelection();
    this.currentStratUnitId = stratUnitId;
    this.publishSelectedIntervalId(this.currentStratUnitId);
  }

  setCurrentSectionId(sectionId: number) {
    const section = this.sections.find((item) => item.id === sectionId);
    if (!section) {
      this.clearCurrentIntervalSelection();
      this.publishSelectedIntervalId(null);
      return;
    }
    this.settings.setDepthRange({
      startDepth: this.convertDepthToSelectedUnit(section.drillingInterval?.from, section.drillingInterval?.uomId),
      endDepth:
        section.drillingInterval?.to == null
          ? null
          : this.convertDepthToSelectedUnit(section.drillingInterval.to, section.drillingInterval.uomId),
    });
    this.clearCurrentIntervalSelection();
    this.currentSectionId = sectionId;
    this.publishSelectedIntervalId(this.currentSectionId);
  }

  getOptionsForInterval(interval: IntervalsTypes, maxLabel: string): { label: string; value: number }[] {
    switch (interval) {
      case IntervalsTypes.FORMATIONS:
        return this.formations.map((formation) => {
          const formationOption = this.intervalOptionsStore.formationsOptionsStore.findById(formation.reservoirId);
          return {
            label: formationOption?.name || '',
            value: formation.id,
          };
        });
      case IntervalsTypes.DRILLING_ANALYSIS:
        return this.drillingAnalysisGroupsForCurrentRegime.map((group) => {
          const fromDepth = this.convertMetersToSelectedUnit(group.fromDepth);
          const toDepth = group.toDepth == null ? maxLabel : this.convertMetersToSelectedUnit(group.toDepth);
          const depthRange =
            group.toDepth == null
              ? `${fromDepth} ${this.settings.depthUnit} — ${toDepth}`
              : `${fromDepth} - ${toDepth} ${this.settings.depthUnit}`;
          const { sectionId, formationId, suiteId } = group.rangeKey || {};
          const section =
            sectionId != null ? this.intervalOptionsStore.sectionsOptionsStore.findById(Number(sectionId)) : null;
          const formation =
            formationId != null ? this.intervalOptionsStore.formationsOptionsStore.findById(Number(formationId)) : null;
          const suite =
            suiteId != null ? this.intervalOptionsStore.stratUnitsOptionsStore.findById(Number(suiteId)) : null;
          const label = [section?.name, formation?.name, suite?.name].filter(Boolean).join(' / ');
          return {
            label: `${label}${label ? ': ' : ''}${depthRange}`,
            value: group.id,
          };
        });
      case IntervalsTypes.STRAT_UNITS:
        return this.stratUnits.map((stratUnit) => {
          const stratUnitOption = this.intervalOptionsStore.stratUnitsOptionsStore.findById(stratUnit.stratUnitTypeId);
          return {
            label: stratUnitOption?.name || '',
            value: stratUnit.id,
          };
        });
      case IntervalsTypes.SECTIONS:
        return this.sections.map((section) => {
          const sectionOption = this.intervalOptionsStore.sectionsOptionsStore.findById(section.sectionTypeId);
          return {
            label: sectionOption?.name || '',
            value: section.id,
          };
        });
      default:
        return [];
    }
  }

  private updateCurveSubscriptions() {
    if (!this.isCurrentLoad(this.loadVersion) || !this.appParams.active || !this.curvesData) {
      return false;
    }
    return this.curveService.updateSubscriptions(this.curvesData);
  }

  private clearCurrentIntervalSelection() {
    this.currentFormationId = null;
    this.currentDrillingAnalysisGroupId = null;
    this.currentStratUnitId = null;
    this.currentSectionId = null;
  }

  private publishSelectedIntervalId(selectedIntervalId: number | null) {
    this.statePublisher.setParams({ selectedIntervalId });
  }

  private restoreSelectedIntervalFromState() {
    const { interval } = this.settings;
    const selectedIntervalId = this.statePublisher.params.selectedIntervalId;

    if (interval === IntervalsTypes.USER_DEFINED) {
      this.clearCurrentIntervalSelection();
      this.publishSelectedIntervalId(null);
      return;
    }

    if (selectedIntervalId == null) {
      return;
    }

    if (interval === IntervalsTypes.FORMATIONS) {
      if (this.formations.some((item) => item.id === selectedIntervalId)) {
        this.setCurrentFormationId(selectedIntervalId);
        return;
      }
    } else if (interval === IntervalsTypes.DRILLING_ANALYSIS) {
      if (this.drillingAnalysisGroupsForCurrentRegime.some((item) => item.id === selectedIntervalId)) {
        this.setCurrentDrillingAnalysisGroupId(selectedIntervalId);
        return;
      }
    } else if (interval === IntervalsTypes.STRAT_UNITS) {
      if (this.stratUnits.some((item) => item.id === selectedIntervalId)) {
        this.setCurrentStratUnitId(selectedIntervalId);
        return;
      }
    } else if (interval === IntervalsTypes.SECTIONS) {
      if (this.sections.some((item) => item.id === selectedIntervalId)) {
        this.setCurrentSectionId(selectedIntervalId);
        return;
      }
    }

    this.selectDefaultIntervalForCurrentType();
  }

  private selectDefaultIntervalForCurrentType() {
    const { interval } = this.settings;

    if (interval === IntervalsTypes.FORMATIONS && this.formations.length > 0) {
      this.setCurrentFormationId(this.formations[0].id);
      return;
    }
    if (interval === IntervalsTypes.DRILLING_ANALYSIS && this.drillingAnalysisGroupsForCurrentRegime.length > 0) {
      this.setCurrentDrillingAnalysisGroupId(this.drillingAnalysisGroupsForCurrentRegime[0].id);
      return;
    }
    if (interval === IntervalsTypes.STRAT_UNITS && this.stratUnits.length > 0) {
      this.setCurrentStratUnitId(this.stratUnits[0].id);
      return;
    }
    if (interval === IntervalsTypes.SECTIONS && this.sections.length > 0) {
      this.setCurrentSectionId(this.sections[0].id);
      return;
    }

    // Keep selectedIntervalId in publisher so switching back to a wellbore
    // with available intervals can restore or fall back to the first option.
    this.clearCurrentIntervalSelection();
  }

  private resetSettingsForWellChange() {
    this.settings.setInterval(IntervalsTypes.USER_DEFINED);
    this.settings.setDepthRange({
      startDepth: DEPTH_MIN,
      endDepth: DEPTH_MAX,
    });
    this.settings.setOperationRegimes(OperationRegimes.ROTATION);
    this.settings.setRotorDrilling(DEFAULT_MODES.rotor);
    this.settings.setSlideDrilling(DEFAULT_MODES.slide);
    this.clearCurrentIntervalSelection();
    this.publishSelectedIntervalId(null);
  }

  private clearSubTimeout() {
    if (this.unsubTimeout) {
      clearTimeout(this.unsubTimeout);
      this.unsubTimeout = null;
    }
  }

  private async fetchBaseParameters(wellboreId: number, version: number) {
    const curvesById = new Map(this.logs.allCurves.map((curve) => [curve.goStreamId, curve]));
    const { curvesData } = await this.curveService.loadBaseParameters(
      wellboreId,
      (id) => curvesById.get(id) ?? null,
      HEATMAP_BASE_PARAMS,
    );
    if (!this.isCurrentLoad(version)) {
      return;
    }
    runInAction(() => {
      this.curvesData = curvesData;
      this.settings.setCurvesData(curvesData);
    });
    this.updateCurveSubscriptions();
    await this.curveService.loadCurves(curvesData);
  }

  get isCurveDataLoading() {
    return this.curveService.isLoading;
  }

  get isDataLoading(): boolean {
    return this.status === 'loading' || this.wellbores.inProgress || this.logs.inProgress || this.isCurveDataLoading;
  }

  get curvesIds() {
    if (!this.curvesData) {
      return null;
    }
    return Object.values(this.curvesData)
      .map((curve) => curve?.goStreamId)
      .filter(hasValue);
  }

  get curvesKeys() {
    if (!this.curvesData) {
      return null;
    }
    return Object.keys(this.curvesData);
  }

  private clearHeatmapData() {
    this.curveCoordinates = null;
    this.dataProcessor.clearLastBinner();
    this.heatmapChartStore.setBinningResult({
      data: [],
      ropRange: null,
      mseRange: null,
      activeRopBins: [],
      activeMseBins: [],
    });
  }

  private updateHeatmapChartData() {
    if (this.destroyed || this.isDataLoading) {
      return;
    }
    this.heatmapChartStore.setBinningResult(
      this.dataProcessor.buildHeatmapData({
        settings: this.settings,
        curvesData: this.curvesData,
        curveCoordinates: this.curveCoordinates,
      }),
    );
  }

  private isActiveParameter(parameter: BaseParameterTypes) {
    return [
      this.settings.currentXAxisParameter,
      this.settings.currentYAxisParameter,
      this.settings.currentMseAxisParameter,
      BaseParameterTypes.RATE_OF_PENETRATION,
      BaseParameterTypes.ROTARY_SPEED,
    ].includes(parameter);
  }

  checkCanCreateHeatmap(parameters: BaseParameterTypes[]): string[] | null {
    return this.dataProcessor.checkCanCreateHeatmap(parameters, this.curvesData);
  }

  private handleCurveCoordinatesChanged = (curveId: number, coordinates: CurvePoint[]) => {
    if (this.destroyed) {
      return;
    }
    let active = false;
    for (const [parameter, curve] of Object.entries(this.curvesData ?? {})) {
      if (curve?.goStreamId === curveId) {
        const key = parameter as BaseParameterTypes;
        this.curveCoordinates ??= {};
        this.curveCoordinates[key] = coordinates;
        active ||= this.isActiveParameter(key);
      }
    }
    if (active) {
      this.updateHeatmapChartData();
    }
  };

  setSettingsDrawerOpen(value: boolean) {
    this.isSettingsDrawerOpen = value;
  }

  destroy() {
    this.destroyed = true;
    this.loadVersion += 1;
    this.wellbores.clear();
    this.statePublisher.debouncedOnChange?.cancel();
    for (const dispose of this.disposers) {
      dispose();
    }
    this.disposers = [];
    this.clearSubTimeout();
    this.curveService.reset();
    this.logs.destroy();
  }
}
